import { lazy, Suspense, useCallback } from 'react';
import {
  type IOutputRendererProps,
  type TResolvedValue,
  ERenderMode,
} from '../types';
import {
  isPrimitive,
  isHtmlString,
  isTable,
  isFile,
  isIDPOutput,
  isIDPList,
  isIDPBundle,
  isIDPBundleList,
  getIDPFieldsFromOutput,
  isBrowserInstance,
  isRemoteValue,
  isReadContentOutput,
} from '../utils';

// Lazy load all renderers for better code splitting
// Each renderer is imported from its own file to enable true code splitting
const PrimitiveRenderer = lazy(() =>
  import('./renderers/PrimitiveRenderer').then(m => ({
    default: m.PrimitiveRenderer,
  })),
);
const ArrayRenderer = lazy(() =>
  import('./renderers/ArrayRenderer').then(m => ({ default: m.ArrayRenderer })),
);
const DictionaryRenderer = lazy(() =>
  import('./renderers/DictionaryRenderer').then(m => ({
    default: m.DictionaryRenderer,
  })),
);
const TableRenderer = lazy(() =>
  import('./renderers/TableRenderer').then(m => ({ default: m.TableRenderer })),
);
const FileRenderer = lazy(() =>
  import('./renderers/FileRenderer').then(m => ({ default: m.FileRenderer })),
);
const IDPPreviewRenderer = lazy(() =>
  import('./renderers/IDPPreviewRenderer').then(m => ({
    default: m.IDPPreviewRenderer,
  })),
);
const ReadContentRenderer = lazy(() =>
  import('./renderers/ReadContentRenderer').then(m => ({
    default: m.ReadContentRenderer,
  })),
);

const BrowserRenderer = lazy(() =>
  import('./renderers/BrowserRenderer').then(m => ({
    default: m.BrowserRenderer,
  })),
);
const HtmlRenderer = lazy(() =>
  import('./renderers/HtmlRenderer').then(m => ({ default: m.HtmlRenderer })),
);
const IDPListPreviewRenderer = lazy(() =>
  import('./renderers/IDPListPreviewRenderer').then(m => ({
    default: m.IDPListPreviewRenderer,
  })),
);
const RemoteValueRenderer = lazy(() =>
  import('./renderers/RemoteValueRenderer').then(m => ({
    default: m.RemoteValueRenderer,
  })),
);

// ============================================================================
// Main Renderer Component
// ============================================================================

/**
 * Main output renderer that routes to appropriate sub-component based on value type
 *
 * @param props - Component props
 * @param props.value - The resolved value to render
 * @param props.mode - Rendering mode (compact, preview, or expanded)
 * @param props.onExpandedClose - Optional callback when closing expanded view
 * @param props.outputName - Output name used for download filenames
 *
 * @remarks
 * Routes to appropriate renderer based on value type:
 * 1. Primitives (null, number, string) → PrimitiveRenderer
 * 2. Arrays (list, set) → ArrayRenderer
 * 3. Tables → TableRenderer
 * 4. Files → FileRenderer
 * 5. Dictionaries (plain objects) → DictionaryRenderer
 * All renderers are lazy-loaded for code splitting
 */
export function OutputRenderer({
  value,
  mode = ERenderMode.PREVIEW,
  onExpandedClose,
  outputName,
  onDownload,
  scrollParent,
  minimal,
  fillContainer,
}: IOutputRendererProps) {
  /**
   * Recursively renders nested values by creating a new OutputRenderer instance.
   * Memoized to prevent unnecessary re-renders of child components.
   *
   * @param nestedValue - The nested value to render
   * @returns React element with OutputRenderer for the nested value
   */
  const renderNestedValue = useCallback(
    (nestedValue: TResolvedValue) => {
      return (
        <OutputRenderer
          value={nestedValue}
          mode={mode}
          onExpandedClose={onExpandedClose}
        />
      );
    },
    [mode, onExpandedClose],
  );

  // Route to appropriate renderer based on type

  // 1a. HTML strings — checked before isPrimitive since every HTML string is
  // also a string. Detected heuristically (see isHtmlString); routed to the
  // output-side HtmlRenderer adapter, which owns mode→sizing and delegates the
  // iframe rendering to file-renderer/HtmlRenderer.
  if (isHtmlString(value)) {
    return (
      <Suspense fallback={null}>
        <HtmlRenderer value={value} mode={mode} outputName={outputName} />
      </Suspense>
    );
  }

  // 1. Handle primitives (null, number, string)
  if (isPrimitive(value)) {
    return (
      <Suspense fallback={null}>
        <PrimitiveRenderer value={value} mode={mode} />
      </Suspense>
    );
  }

  // 2. Check for read_content results (markdown/text content)
  if (isReadContentOutput(value)) {
    return (
      <Suspense fallback={null}>
        <ReadContentRenderer value={value} mode={mode} />
      </Suspense>
    );
  }

  // 3. Check for IDP data (new extraction_result format)
  if (isIDPOutput(value)) {
    const idpFields = getIDPFieldsFromOutput(value);
    const dict = value as Record<string, TResolvedValue>;
    // document_analysis uses 'source', extraction_result/table_extraction use 'document'
    const documentName = (dict.document ||
      dict.source ||
      idpFields[0]?.document) as string;

    return (
      <Suspense fallback={null}>
        <IDPPreviewRenderer
          documentName={documentName}
          fields={idpFields}
          mode={mode}
          onExpandedClose={onExpandedClose}
        />
      </Suspense>
    );
  }

  // 4. Handle IDP lists (arrays of IDP outputs or IDP bundles) - before generic array
  if (Array.isArray(value) && (isIDPList(value) || isIDPBundleList(value))) {
    return (
      <Suspense fallback={null}>
        <IDPListPreviewRenderer
          items={value}
          mode={mode}
          onExpandedClose={onExpandedClose}
        />
      </Suspense>
    );
  }

  // 4b. Handle single IDP bundle (dict wrapping multiple IDP outputs)
  // Wrap as a one-item list so the inline panel still uses the tab layout.
  if (isIDPBundle(value)) {
    return (
      <Suspense fallback={null}>
        <IDPListPreviewRenderer
          items={[value]}
          mode={mode}
          onExpandedClose={onExpandedClose}
        />
      </Suspense>
    );
  }

  // 5. Handle arrays (list, set)
  if (Array.isArray(value)) {
    return (
      <Suspense fallback={null}>
        <ArrayRenderer
          value={value}
          renderValue={renderNestedValue}
          mode={mode}
        />
      </Suspense>
    );
  }

  // 6. Handle offloaded values — fetch via paginateValueContent, then re-dispatch.
  // Checked before table/file/browser/dictionary because a remote-value is also
  // a `{ type: ... }` object but doesn't match any of those discriminators.
  if (isRemoteValue(value)) {
    return (
      <Suspense fallback={null}>
        <RemoteValueRenderer
          value={value}
          mode={mode}
          onExpandedClose={onExpandedClose}
          outputName={outputName}
          onDownload={onDownload}
          renderValue={renderNestedValue}
        />
      </Suspense>
    );
  }

  // 7. Handle table objects (must check before general objects)
  if (isTable(value)) {
    return (
      <Suspense fallback={null}>
        <TableRenderer
          value={value}
          mode={mode}
          onExpandedClose={onExpandedClose}
          outputName={outputName}
          onDownload={onDownload}
        />
      </Suspense>
    );
  }

  // 8. Handle file objects (must check before general objects)
  if (isFile(value)) {
    return (
      <Suspense fallback={null}>
        <FileRenderer
          testId="file-renderer-output"
          value={value}
          mode={mode}
          onExpandedClose={onExpandedClose}
          onDownload={onDownload}
          scrollParent={scrollParent}
          fillContainer={fillContainer}
        />
      </Suspense>
    );
  }

  // 9. Handle browser instances (must check before general dictionaries)
  if (isBrowserInstance(value)) {
    return (
      <Suspense fallback={null}>
        <BrowserRenderer
          value={value}
          mode={mode}
          onExpandedClose={onExpandedClose}
          minimal={minimal}
        />
      </Suspense>
    );
  }

  // 10. Handle dictionaries (plain objects)

  if (typeof value === 'object' && value !== null) {
    return (
      <Suspense fallback={null}>
        <DictionaryRenderer
          value={value as { [key: string]: TResolvedValue }}
          renderValue={renderNestedValue}
          mode={mode}
          onExpandedClose={onExpandedClose}
          outputName={outputName}
          onDownload={onDownload}
        />
      </Suspense>
    );
  }

  // Fallback for unknown types (should never reach here)
  return (
    <Suspense fallback={null}>
      <PrimitiveRenderer value={String(value)} mode={mode} />
    </Suspense>
  );
}
